import { Wand2 } from 'lucide-react'
import type { ValidationError } from './validator/types'
import { canAutoResolve } from './validator/resolutions'

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  errors: ValidationError[]
  onAutoResolve: (error: ValidationError) => void
  onAcknowledge: (error: ValidationError) => void
}

// ─── Severity config ──────────────────────────────────────────────────────────

const SEVERITY_STYLES = {
  error:   { bar: 'bg-error-fg',   text: 'text-error-fg',   label: 'Error'   },
  warning: { bar: 'bg-warning-fg', text: 'text-warning-fg', label: 'Warning' },
  info:    { bar: 'bg-info-fg',    text: 'text-info-fg',    label: 'Info'    },
}

// ─── Single annotation card ───────────────────────────────────────────────────

function AnnotationCard({
  error,
  onAutoResolve,
  onAcknowledge,
}: {
  error: ValidationError
  onAutoResolve: (e: ValidationError) => void
  onAcknowledge: (e: ValidationError) => void
}) {
  const style = SEVERITY_STYLES[error.severity]
  const resolvable = canAutoResolve(error.code)

  return (
    <div className={`relative overflow-hidden rounded border border-line bg-canvas shadow-sm ${error.acknowledged ? 'opacity-50' : ''}`}>
      {/* Left severity bar */}
      <div className={`absolute inset-y-0 left-0 w-1 ${style.bar}`} />

      <div className="pl-3 pr-2.5 py-2 space-y-1.5">
        {/* Severity label + message */}
        <p className={`text-[10px] font-bold uppercase tracking-wider ${style.text}`}>
          {style.label}
        </p>
        <p className="text-[11px] leading-snug text-fg">
          {error.message}
        </p>

        {/* Actions */}
        {!error.acknowledged && (
          <div className="flex items-center gap-1.5 pt-0.5">
            {resolvable && (
              <button
                type="button"
                onClick={() => onAutoResolve(error)}
                className="flex items-center gap-1 rounded bg-accent-primary-solid px-1.5 py-0.5 text-[10px] font-semibold text-white transition-opacity hover:opacity-90"
              >
                <Wand2 size={9} />
                Auto-fix
              </button>
            )}
            {error.severity !== 'error' && (
              <button
                type="button"
                onClick={() => onAcknowledge(error)}
                className="rounded px-1.5 py-0.5 text-[10px] text-fg-secondary hover:bg-fill hover:text-fg"
              >
                Dismiss
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── ValidationAnnotations ────────────────────────────────────────────────────

/**
 * Renders a stack of annotation cards for a given block's errors.
 * Placed in the 210px right gutter of the canvas two-column layout.
 */
export function ValidationAnnotations({ errors, onAutoResolve, onAcknowledge }: Props) {
  if (errors.length === 0) return null
  return (
    <div className="space-y-1.5">
      {errors.map((err, i) => (
        <AnnotationCard
          key={`${err.code}-${i}`}
          error={err}
          onAutoResolve={onAutoResolve}
          onAcknowledge={onAcknowledge}
        />
      ))}
    </div>
  )
}
